import { Bubbles } from "components/molecules";
import {
  BackButton,
  Card,
  CardTitle,
  Content,
  Page,
  TopBar,
} from "components/templates";
import { WorldMap } from "components/organisms";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { useAppLanguage } from "context/AppLanguageContext";
import {
  AutotestDifficulty,
  AutotestQuestion,
  buildAutotestQuestions,
  dailySeed,
  seededRng,
} from "utils/autotestQuestions";
import countriesData from "utils/countriesData.json";
import { getRandomQuote, InspirationalQuote } from "utils/inspirationalQuotes";
import { AvailableLanguages, Country } from "utils/types";

type Phase = "start" | "playing" | "result";

/**
 * Modes change the *shape* of a run, not just its content:
 * classic ends after N questions, timeAttack after 90s, survival after three
 * mistakes, and daily is the same seeded run for everyone on a given date.
 */
export type AutotestMode = "classic" | "timeAttack" | "survival" | "daily";

const MODES: AutotestMode[] = ["classic", "timeAttack", "survival", "daily"];
const QUIZ_LENGTHS = [5, 10, 15] as const;
const DIFFICULTIES: AutotestDifficulty[] = ["easy", "medium", "hard"];
const BEST_SCORES_KEY = "around-the-world.autotest.bestScores";
const DAILY_KEY = "around-the-world.autotest.daily";

const TIME_ATTACK_SECONDS = 90;
const SURVIVAL_LIVES = 3;
// Endless modes draw from one big pre-built batch rather than generating a
// question per tick; nobody gets through 40 in 90 seconds.
const ENDLESS_BATCH = 40;
const DAILY_LENGTH = 10;

interface DailyState {
  /** ISO date (YYYY-MM-DD) of the last completed daily run. */
  lastPlayed?: string;
  streak: number;
  bestScore: number;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const readDaily = (): DailyState => {
  try {
    const parsed = JSON.parse(localStorage.getItem(DAILY_KEY) ?? "null");
    if (!parsed || typeof parsed !== "object")
      return { streak: 0, bestScore: 0 };
    return {
      lastPlayed:
        typeof parsed.lastPlayed === "string" ? parsed.lastPlayed : undefined,
      streak: typeof parsed.streak === "number" ? parsed.streak : 0,
      bestScore: typeof parsed.bestScore === "number" ? parsed.bestScore : 0,
    };
  } catch {
    return { streak: 0, bestScore: 0 };
  }
};

const writeDaily = (state: DailyState) => {
  try {
    localStorage.setItem(DAILY_KEY, JSON.stringify(state));
  } catch {
    // ignore (private mode, storage disabled, etc.)
  }
};

const isYesterday = (iso: string) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().slice(0, 10) === iso;
};

const FALLBACK_FLAG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='92' height='62'%3E%3Crect width='92' height='62' fill='rgba(245,226,200,0.08)'/%3E%3Cpath d='M14 42c12-10 20-18 32-18s20 8 32 18' fill='none' stroke='rgba(245,226,200,0.35)' stroke-width='3'/%3E%3Ccircle cx='30' cy='20' r='5' fill='rgba(245,226,200,0.35)'/%3E%3C/svg%3E";

type BestScores = Record<AutotestDifficulty, Record<number, number>>;

const emptyBestScores = (): BestScores => ({ easy: {}, medium: {}, hard: {} });

const readBestScores = (): BestScores => {
  try {
    const raw = localStorage.getItem(BEST_SCORES_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return emptyBestScores();
    return { ...emptyBestScores(), ...parsed };
  } catch {
    return emptyBestScores();
  }
};

const writeBestScores = (scores: BestScores) => {
  try {
    localStorage.setItem(BEST_SCORES_KEY, JSON.stringify(scores));
  } catch {
    // ignore (private mode, storage disabled, etc.)
  }
};

const IntroText = styled.p`
  margin: 0 0 20px;
  max-width: 100%;
  font-family: "Gaegu", cursive;
  font-size: 1.25rem;
  line-height: 1.4;
  color: rgba(245, 226, 200, 0.9);
`;

const LengthRow = styled.div`
  display: flex;
  gap: 10px;
  margin: 4px 0 18px;
  flex-wrap: wrap;
`;

const LengthButton = styled.button<{ $active: boolean }>`
  cursor: pointer;
  border-radius: 999px;
  padding: 8px 18px;
  font-family: "Finger Paint", cursive;
  font-size: 0.95rem;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(173, 216, 230, 0.85)" : "rgba(245, 226, 200, 0.3)"};
  background: ${({ $active }) =>
    $active ? "rgba(173, 216, 230, 0.35)" : "rgba(27, 38, 79, 0.35)"};
  color: #f5e2c8;
  transition:
    background 120ms ease,
    border 120ms ease;

  &:hover {
    border-color: rgba(173, 216, 230, 0.85);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const FieldLabel = styled.p`
  margin: 0 0 8px;
  font-family: "Finger Paint", cursive;
  font-size: 0.85rem;
  letter-spacing: 0.2px;
  color: rgba(245, 226, 200, 0.7);
`;

const DifficultyHint = styled.p`
  margin: 0 0 18px;
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  color: rgba(245, 226, 200, 0.75);
`;

const BestScore = styled.p`
  margin: 0 0 20px;
  font-family: "Gaegu", cursive;
  font-size: 1.1rem;
  color: rgba(173, 216, 230, 0.9);
`;

const PrimaryButton = styled.button`
  cursor: pointer;
  border-radius: 16px;
  padding: 12px 26px;
  font-family: "Finger Paint", cursive;
  font-size: 1.05rem;
  border: 1px solid rgba(173, 216, 230, 0.6);
  background: linear-gradient(
    180deg,
    rgba(173, 216, 230, 0.42),
    rgba(173, 216, 230, 0.22)
  );
  color: #f5e2c8;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
  transition:
    transform 120ms ease,
    background 120ms ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const secondaryButtonStyles = `
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  border-radius: 16px;
  padding: 12px 22px;
  font-family: "Finger Paint", cursive;
  font-size: 0.95rem;
  border: 1px solid rgba(245, 226, 200, 0.3);
  background: rgba(27, 38, 79, 0.32);
  color: #f5e2c8;
  text-decoration: none;
  transition:
    transform 120ms ease,
    background 120ms ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(27, 38, 79, 0.5);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const SecondaryLink = styled(Link)`
  ${secondaryButtonStyles}
`;

const SecondaryButton = styled.button`
  ${secondaryButtonStyles}
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const ProgressRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
  font-family: "Finger Paint", cursive;
  font-size: 0.9rem;
  color: rgba(245, 226, 200, 0.75);
`;

const ProgressTrack = styled.div`
  height: 6px;
  border-radius: 999px;
  background: rgba(24, 40, 72, 0.4);
  overflow: hidden;
  margin-bottom: 20px;
`;

const ProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(173, 216, 230, 0.6),
    rgba(173, 216, 230, 0.95)
  );
  width: ${({ $percent }) => $percent}%;
  transition: width 200ms ease;
`;

const Prompt = styled.h2`
  margin: 0 0 18px;
  font-family: "Gaegu", cursive;
  font-size: 1.6rem;
  line-height: 1.3;
  color: #f5e2c8;
`;

const FlagPreview = styled.img`
  display: block;
  width: 220px;
  height: 130px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid rgba(245, 226, 200, 0.28);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.35);
  background: rgba(0, 0, 0, 0.15);
  margin: 0 0 18px;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;

  @media (min-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const OptionButton = styled.button<{
  $state: "idle" | "correct" | "incorrect" | "reveal";
}>`
  cursor: pointer;
  text-align: left;
  border-radius: 14px;
  padding: 12px 14px;
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  line-height: 1.3;
  color: #f5e2c8;
  border: 1px solid
    ${({ $state }) =>
      $state === "correct"
        ? "rgba(120, 220, 150, 0.9)"
        : $state === "incorrect"
          ? "rgba(230, 120, 120, 0.9)"
          : "rgba(245, 226, 200, 0.28)"};
  background: ${({ $state }) =>
    $state === "correct"
      ? "rgba(120, 220, 150, 0.28)"
      : $state === "incorrect"
        ? "rgba(230, 120, 120, 0.24)"
        : "rgba(27, 38, 79, 0.32)"};
  transition:
    transform 120ms ease,
    background 120ms ease,
    border 120ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(173, 216, 230, 0.7);
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const FeedbackText = styled.p`
  min-height: 1.4em;
  margin: 14px 0 6px;
  font-family: "Finger Paint", cursive;
  font-size: 0.95rem;
  color: rgba(245, 226, 200, 0.92);
`;

const popIn = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const glow = keyframes`
  0%, 100% { text-shadow: 2px 5px 3px rgba(0, 0, 0, 0.35), 0 0 18px rgba(255, 209, 102, 0.15); }
  50% { text-shadow: 2px 5px 3px rgba(0, 0, 0, 0.35), 0 0 28px rgba(255, 209, 102, 0.65); }
`;

const ScoreDisplay = styled.p<{ $celebrate: boolean }>`
  margin: 4px 0 6px;
  font-family: "Gaegu", cursive;
  font-size: 3rem;
  color: #f5e2c8;
  text-shadow: 2px 5px 3px rgba(0, 0, 0, 0.35);
  ${({ $celebrate }) =>
    $celebrate &&
    css`
      animation:
        ${popIn} 480ms cubic-bezier(0.34, 1.56, 0.64, 1),
        ${glow} 1.8s ease-in-out 480ms infinite;
    `}
`;

const ResultMessage = styled.p`
  margin: 0 0 20px;
  font-family: "Gaegu", cursive;
  font-size: 1.3rem;
  color: rgba(245, 226, 200, 0.92);
`;

const QuoteBlock = styled.blockquote`
  margin: 22px 0 0;
  padding: 14px 16px;
  border-left: 3px solid rgba(173, 216, 230, 0.6);
  background: rgba(24, 40, 72, 0.28);
  border-radius: 0 12px 12px 0;
`;

const QuoteText = styled.p`
  margin: 0 0 6px;
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  font-style: italic;
  line-height: 1.35;
  color: rgba(245, 226, 200, 0.95);
`;

const QuoteAuthor = styled.footer`
  font-family: "Finger Paint", cursive;
  font-size: 0.85rem;
  color: rgba(173, 216, 230, 0.85);

  &::before {
    content: "— ";
  }
`;

const fall = keyframes`
  0% {
    transform: translateY(-24px) rotate(0deg);
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(540deg);
    opacity: 0;
  }
`;

const ConfettiLayer = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 20;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const ConfettiPiece = styled.span<{
  $left: number;
  $delay: number;
  $duration: number;
  $color: string;
  $size: number;
}>`
  position: absolute;
  top: -24px;
  left: ${({ $left }) => $left}%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size * 2}px;
  background: ${({ $color }) => $color};
  border-radius: 2px;
  opacity: 0;
  animation: ${fall} ${({ $duration }) => $duration}s linear
    ${({ $delay }) => $delay}s forwards;
`;

interface ConfettiPieceData {
  id: number;
  left: number;
  delay: number;
  duration: number;
  color: string;
  size: number;
}

const CONFETTI_COLORS = [
  "#f5e2c8",
  "#add8e6",
  "#78dc96",
  "#ffd166",
  "#f28482",
  "#a78bfa",
];

const buildConfettiPieces = (count: number): ConfettiPieceData[] =>
  Array.from({ length: count }, (_, id) => ({
    id,
    left: Math.random() * 100,
    delay: Math.random() * 0.7,
    duration: 2.6 + Math.random() * 1.6,
    color: CONFETTI_COLORS[id % CONFETTI_COLORS.length],
    size: 6 + Math.random() * 6,
  }));

const Confetti = ({ pieces }: { pieces: ConfettiPieceData[] }) => (
  <ConfettiLayer aria-hidden="true">
    {pieces.map((p) => (
      <ConfettiPiece
        key={p.id}
        $left={p.left}
        $delay={p.delay}
        $duration={p.duration}
        $color={p.color}
        $size={p.size}
      />
    ))}
  </ConfettiLayer>
);

// --- new-question-type and mode chrome -------------------------------------

const FlagOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin-top: 6px;
`;

const FlagOptionButton = styled.button<{
  $state: "idle" | "correct" | "incorrect";
}>`
  cursor: pointer;
  padding: 8px;
  border-radius: 14px;
  background: rgba(24, 40, 72, 0.35);
  border: 2px solid
    ${({ $state }) =>
      $state === "correct"
        ? "rgba(74, 222, 128, 0.9)"
        : $state === "incorrect"
          ? "rgba(248, 113, 113, 0.9)"
          : "rgba(245, 226, 200, 0.25)"};
  transition:
    transform 120ms ease,
    border-color 120ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    border-color: rgba(173, 216, 230, 0.8);
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }

  & > img {
    width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    border-radius: 8px;
    display: block;
  }
`;

// "Find X on the map": the map itself is the answer surface.
const MapAnswerPanel = styled.div`
  position: relative;
  height: min(52vh, 420px);
  min-height: 260px;
  margin-top: 6px;
  border-radius: 16px;
  border: 1px solid rgba(245, 226, 200, 0.2);
  background: rgba(24, 40, 72, 0.3);
  overflow: hidden;
`;

const Explanation = styled.div`
  margin: 12px 0;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(173, 216, 230, 0.28);
  background: rgba(24, 40, 72, 0.4);
`;

const ExplanationText = styled.p`
  margin: 0;
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  line-height: 1.35;
  color: rgba(245, 226, 200, 0.95);
`;

const ExplanationLink = styled(Link)`
  display: inline-block;
  margin-top: 8px;
  color: rgba(173, 216, 230, 0.95);
  font-family: "Finger Paint", cursive;
  font-size: 0.8rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const StatusPill = styled.span<{ $urgent?: boolean }>`
  padding: 5px 11px;
  border-radius: 999px;
  font-family: "Finger Paint", cursive;
  font-size: 0.78rem;
  color: ${({ $urgent }) => ($urgent ? "#3b0a0a" : "#f5e2c8")};
  background: ${({ $urgent }) =>
    $urgent ? "rgba(248, 113, 113, 0.9)" : "rgba(27, 38, 79, 0.45)"};
  border: 1px solid rgba(245, 226, 200, 0.28);
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 10px;
  margin-bottom: 4px;
`;

const ModeCard = styled.button<{ $active: boolean }>`
  cursor: pointer;
  text-align: left;
  padding: 12px 14px;
  border-radius: 16px;
  color: #f5e2c8;
  background: ${({ $active }) =>
    $active ? "rgba(173, 216, 230, 0.22)" : "rgba(24, 40, 72, 0.3)"};
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(173, 216, 230, 0.9)" : "rgba(245, 226, 200, 0.22)"};
  transition:
    transform 120ms ease,
    border-color 120ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(173, 216, 230, 0.7);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const ModeName = styled.span`
  display: block;
  font-family: "Finger Paint", cursive;
  font-size: 0.92rem;
  margin-bottom: 4px;
`;

const ModeDesc = styled.span`
  display: block;
  font-family: "Gaegu", cursive;
  font-size: 1.02rem;
  line-height: 1.25;
  color: rgba(245, 226, 200, 0.75);
`;

const VisuallyHiddenLive = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

const ui = {
  en: {
    title: "Autotest",
    intro:
      "How much do you really know about the world's countries? Pick a length and difficulty and find out — capitals, flags, continents and currencies, all mixed up.",
    lengthLabel: (n: number) => `${n} questions`,
    difficultyLabel: "Difficulty",
    difficulty: { easy: "Easy", medium: "Medium", hard: "Hard" },
    difficultyHint: {
      easy: "Recognition: well-known countries, with the flag on screen to help.",
      medium: "Recall: any country, four options, no visual crutches.",
      hard: "Reasoning: combine two facts, rule one out, and watch for traps.",
    },
    modeLabel: "Mode",
    mode: {
      classic: "Classic",
      timeAttack: "Time attack",
      survival: "Survival",
      daily: "Daily challenge",
    },
    modeDesc: {
      classic: "A fixed number of questions, at your own pace.",
      timeAttack: `As many as you can in ${TIME_ATTACK_SECONDS} seconds.`,
      survival: `Keep going until you get ${SURVIVAL_LIVES} wrong.`,
      daily:
        "The same 10 questions for everyone today. Keep your streak alive.",
    },
    timeLeft: (s: number) => `${s}s`,
    livesLeft: (n: number) =>
      `${"♥".repeat(n)}${"♡".repeat(SURVIVAL_LIVES - n)}`,
    answered: (n: number) => `${n} answered`,
    streak: (n: number) => `Streak: ${n} 🔥`,
    dailyDone: "You already played today's challenge — come back tomorrow.",
    playAnyway: "Play it again (won't change your streak)",
    whyLabel: "Why",
    readMore: (c: string) => `Read more about ${c} →`,
    tapTheMap: "Tap the country on the map.",
    timeUp: "Time's up!",
    outOfLives: "Out of lives!",
    finalAnswered: (correct: number, total: number) =>
      `${correct} correct out of ${total} answered`,
    bestScore: (score: number, total: number) =>
      `Your best score for ${total} questions: ${score}/${total}`,
    start: "Start",
    back: "Back",
    backAria: "Go back",
    question: (i: number, total: number) => `Question ${i} of ${total}`,
    next: "Next →",
    seeResult: "See result →",
    correct: "Correct!",
    incorrect: (answer: string) =>
      `Not quite — the right answer was ${answer}.`,
    yourScore: "Your score",
    tryAgain: "Try again",
    changeLength: "Quiz length",
    changeSettings: "Change settings",
    backToMap: "Back to the map",
    messageGreat: "Excellent! You really know your geography.",
    messageGood: "Nice work! A solid grasp of the world map.",
    messageOk: "Not bad — explore a few more countries and try again.",
    messageKeepPracticing:
      "Time to keep practicing — explore the map and give it another shot.",
  },
  es: {
    title: "Autotest",
    intro:
      "¿Cuánto sabés realmente sobre los países del mundo? Elegí cantidad de preguntas y dificultad, y descubrilo: capitales, banderas, continentes y monedas, todo mezclado.",
    lengthLabel: (n: number) => `${n} preguntas`,
    difficultyLabel: "Dificultad",
    difficulty: { easy: "Fácil", medium: "Medio", hard: "Difícil" },
    difficultyHint: {
      easy: "Reconocimiento: países muy conocidos, con la bandera a la vista.",
      medium:
        "Evocación: cualquier país, cuatro opciones, sin ayudas visuales.",
      hard: "Razonamiento: combinar dos datos, descartar, y ojo con las trampas.",
    },
    modeLabel: "Modo",
    mode: {
      classic: "Clásico",
      timeAttack: "Contrarreloj",
      survival: "Supervivencia",
      daily: "Desafío diario",
    },
    modeDesc: {
      classic: "Una cantidad fija de preguntas, a tu ritmo.",
      timeAttack: `Las que puedas en ${TIME_ATTACK_SECONDS} segundos.`,
      survival: `Seguí hasta errar ${SURVIVAL_LIVES} veces.`,
      daily: "Las mismas 10 preguntas para todos hoy. Mantené tu racha.",
    },
    timeLeft: (s: number) => `${s}s`,
    livesLeft: (n: number) =>
      `${"♥".repeat(n)}${"♡".repeat(SURVIVAL_LIVES - n)}`,
    answered: (n: number) => `${n} respondidas`,
    streak: (n: number) => `Racha: ${n} 🔥`,
    dailyDone: "Ya jugaste el desafío de hoy — volvé mañana.",
    playAnyway: "Jugarlo igual (no cambia tu racha)",
    whyLabel: "Por qué",
    readMore: (c: string) => `Leer más sobre ${c} →`,
    tapTheMap: "Tocá el país en el mapa.",
    timeUp: "¡Se acabó el tiempo!",
    outOfLives: "¡Te quedaste sin vidas!",
    finalAnswered: (correct: number, total: number) =>
      `${correct} correctas de ${total} respondidas`,
    bestScore: (score: number, total: number) =>
      `Tu mejor puntaje para ${total} preguntas: ${score}/${total}`,
    start: "Comenzar",
    back: "Volver",
    backAria: "Volver",
    question: (i: number, total: number) => `Pregunta ${i} de ${total}`,
    next: "Siguiente →",
    seeResult: "Ver resultado →",
    correct: "¡Correcto!",
    incorrect: (answer: string) =>
      `No era esa — la respuesta correcta era ${answer}.`,
    yourScore: "Tu puntaje",
    tryAgain: "Intentar de nuevo",
    changeLength: "Cantidad de preguntas",
    changeSettings: "Cambiar opciones",
    backToMap: "Volver al mapa",
    messageGreat: "¡Excelente! Conocés muy bien la geografía del mundo.",
    messageGood: "¡Bien hecho! Tenés un buen manejo del mapa mundial.",
    messageOk: "Nada mal — explorá algunos países más y volvé a intentar.",
    messageKeepPracticing:
      "Hay que seguir practicando — explorá el mapa y volvé a intentarlo.",
  },
};

// Marker kept in the history entry a run pushes, so "back" can tell a run in
// progress apart from the setup screen.
const RUN_STATE_KEY = "autotestRun";

export const Autotest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isInRunEntry =
    (location.state as { autotestRun?: boolean } | null)?.autotestRun === true;
  const { language } = useAppLanguage();
  const isSpanish = language === AvailableLanguages.ES;
  const resolvedLanguage = language ?? AvailableLanguages.EN;
  const t = isSpanish ? ui.es : ui.en;

  const [phase, setPhase] = useState<Phase>("start");
  const [mode, setMode] = useState<AutotestMode>("classic");
  const [length, setLength] = useState<number>(10);
  const [difficulty, setDifficulty] = useState<AutotestDifficulty>("medium");
  const [questions, setQuestions] = useState<AutotestQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [mapAnswer, setMapAnswer] = useState<Country | null>(null);
  const [score, setScore] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [lives, setLives] = useState(SURVIVAL_LIVES);
  const [timeLeft, setTimeLeft] = useState(TIME_ATTACK_SECONDS);
  const [endReason, setEndReason] = useState<"finished" | "time" | "lives">(
    "finished"
  );
  const [flagFailed, setFlagFailed] = useState(false);
  const [bestScores, setBestScores] = useState<BestScores>(emptyBestScores());
  const [daily, setDaily] = useState<DailyState>({ streak: 0, bestScore: 0 });
  const [confettiPieces, setConfettiPieces] = useState<
    ConfettiPieceData[] | null
  >(null);
  const [resultQuote, setResultQuote] = useState<InspirationalQuote | null>(
    null
  );

  useEffect(() => {
    setBestScores(readBestScores());
    setDaily(readDaily());
  }, []);

  const currentQuestion = questions[currentIndex];
  // Map questions are answered by tapping the map, not by picking an option.
  const answered =
    currentQuestion?.type === "mapPoint"
      ? mapAnswer !== null
      : selectedOption !== null;
  const isEndless = mode === "timeAttack" || mode === "survival";

  const finishRun = useCallback(
    (
      reason: "finished" | "time" | "lives",
      finalScore: number,
      total: number
    ) => {
      setEndReason(reason);

      if (mode === "classic") {
        setBestScores((prev) => {
          const forDifficulty = prev[difficulty] ?? {};
          const next = {
            ...prev,
            [difficulty]: {
              ...forDifficulty,
              [length]: Math.max(forDifficulty[length] ?? 0, finalScore),
            },
          };
          writeBestScores(next);
          return next;
        });
      }

      if (mode === "daily") {
        setDaily((prev) => {
          const today = todayIso();
          // Replaying the same day must not inflate the streak.
          if (prev.lastPlayed === today) return prev;
          const next: DailyState = {
            lastPlayed: today,
            streak:
              prev.lastPlayed && isYesterday(prev.lastPlayed)
                ? prev.streak + 1
                : 1,
            bestScore: Math.max(prev.bestScore, finalScore),
          };
          writeDaily(next);
          return next;
        });
      }

      const ratio = total > 0 ? finalScore / total : 0;
      setConfettiPieces(ratio >= 0.8 ? buildConfettiPieces(70) : null);
      setResultQuote(getRandomQuote());
      setPhase("result");
    },
    [difficulty, length, mode]
  );

  const startQuiz = (
    selectedMode: AutotestMode,
    selectedLength: number,
    selectedDifficulty: AutotestDifficulty
  ) => {
    setMode(selectedMode);
    setLength(selectedLength);
    setDifficulty(selectedDifficulty);

    const count =
      selectedMode === "daily"
        ? DAILY_LENGTH
        : selectedMode === "classic"
          ? selectedLength
          : ENDLESS_BATCH;

    setQuestions(
      buildAutotestQuestions(
        count,
        resolvedLanguage,
        // Everyone gets the same daily run: same seed, same difficulty.
        selectedMode === "daily" ? "medium" : selectedDifficulty,
        selectedMode === "daily" ? { rng: seededRng(dailySeed()) } : undefined
      )
    );

    setCurrentIndex(0);
    setSelectedOption(null);
    setMapAnswer(null);
    setScore(0);
    setAnsweredCount(0);
    setLives(SURVIVAL_LIVES);
    setTimeLeft(TIME_ATTACK_SECONDS);
    setEndReason("finished");
    setFlagFailed(false);
    setConfettiPieces(null);
    setResultQuote(null);
    setPhase("playing");

    // The quiz's phases live in component state, not in the URL, so without
    // this the browser's back gesture jumped straight out of /autotest to the
    // map. Starting a run pushes one history entry, which back then pops to
    // return to the setup screen. Retries replace it instead of stacking, so
    // "play again" five times doesn't cost five back presses to leave.
    navigate(".", {
      state: { [RUN_STATE_KEY]: true },
      replace: isInRunEntry,
    });
  };

  // Back landed on the entry without the run marker: the player is leaving
  // the run, so show the setup screen rather than an orphaned quiz.
  useEffect(() => {
    if (!isInRunEntry) setPhase("start");
  }, [isInRunEntry]);

  // Time attack countdown.
  useEffect(() => {
    if (phase !== "playing" || mode !== "timeAttack") return;
    const id = window.setInterval(() => {
      setTimeLeft((remaining) => Math.max(0, remaining - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, mode]);

  useEffect(() => {
    if (phase === "playing" && mode === "timeAttack" && timeLeft === 0) {
      finishRun("time", score, answeredCount);
    }
  }, [phase, mode, timeLeft, score, answeredCount, finishRun]);

  const registerAnswer = (wasCorrect: boolean) => {
    setAnsweredCount((n) => n + 1);
    if (wasCorrect) {
      setScore((s) => s + 1);
    } else if (mode === "survival") {
      setLives((remaining) => remaining - 1);
    }
  };

  const handleSelect = (index: number) => {
    if (answered || !currentQuestion) return;
    setSelectedOption(index);
    registerAnswer(index === currentQuestion.correctIndex);
  };

  const handleMapSelect = (country: Country) => {
    if (answered || !currentQuestion) return;
    setMapAnswer(country);
    registerAnswer(country["english-name"] === currentQuestion.mapTargetName);
  };

  const mapWasCorrect =
    mapAnswer !== null &&
    mapAnswer["english-name"] === currentQuestion?.mapTargetName;
  const wasCorrect =
    currentQuestion?.type === "mapPoint"
      ? mapWasCorrect
      : selectedOption === currentQuestion?.correctIndex;

  const isLastQuestion =
    mode === "survival" && lives <= 0
      ? true
      : isEndless
        ? currentIndex + 1 >= questions.length
        : currentIndex + 1 >= questions.length;

  const handleNext = () => {
    if (mode === "survival" && lives <= 0) {
      finishRun("lives", score, answeredCount);
      return;
    }

    if (currentIndex + 1 < questions.length) {
      setCurrentIndex((i) => i + 1);
      setSelectedOption(null);
      setMapAnswer(null);
      setFlagFailed(false);
      return;
    }

    finishRun("finished", score, isEndless ? answeredCount : questions.length);
  };

  const percent = useMemo(() => {
    if (mode === "timeAttack")
      return ((TIME_ATTACK_SECONDS - timeLeft) / TIME_ATTACK_SECONDS) * 100;
    if (mode === "survival")
      return ((SURVIVAL_LIVES - lives) / SURVIVAL_LIVES) * 100;
    return questions.length ? ((currentIndex + 1) / questions.length) * 100 : 0;
  }, [mode, timeLeft, lives, currentIndex, questions.length]);

  const resultTotal = isEndless ? answeredCount : questions.length;
  const resultRatio = resultTotal ? score / resultTotal : 0;

  const resultMessage = useMemo(() => {
    if (resultTotal === 0) return "";
    if (resultRatio >= 0.8) return t.messageGreat;
    if (resultRatio >= 0.5) return t.messageGood;
    if (resultRatio >= 0.3) return t.messageOk;
    return t.messageKeepPracticing;
  }, [resultRatio, resultTotal, t]);

  const dailyPlayedToday = daily.lastPlayed === todayIso();

  return (
    <Page id="autotest" lang={isSpanish ? "es" : "en"}>
      <Bubbles />
      <Content>
        <TopBar>
          <BackButton onClick={() => navigate(-1)} aria-label={t.backAria}>
            <span aria-hidden="true">←</span> {t.back}
          </BackButton>
        </TopBar>

        {phase === "start" && (
          <Card>
            <CardTitle>{t.title}</CardTitle>
            <IntroText>{t.intro}</IntroText>

            <FieldLabel>{t.modeLabel}</FieldLabel>
            <ModeGrid role="radiogroup" aria-label={t.modeLabel}>
              {MODES.map((m) => (
                <ModeCard
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={mode === m}
                  $active={mode === m}
                  onClick={() => setMode(m)}
                >
                  <ModeName>{t.mode[m]}</ModeName>
                  <ModeDesc>{t.modeDesc[m]}</ModeDesc>
                </ModeCard>
              ))}
            </ModeGrid>

            {mode === "classic" && (
              <>
                <FieldLabel>{t.changeLength}</FieldLabel>
                <LengthRow role="radiogroup" aria-label={t.changeLength}>
                  {QUIZ_LENGTHS.map((n) => (
                    <LengthButton
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={length === n}
                      $active={length === n}
                      onClick={() => setLength(n)}
                    >
                      {t.lengthLabel(n)}
                    </LengthButton>
                  ))}
                </LengthRow>
              </>
            )}

            {mode !== "daily" && (
              <>
                <FieldLabel>{t.difficultyLabel}</FieldLabel>
                <LengthRow role="radiogroup" aria-label={t.difficultyLabel}>
                  {DIFFICULTIES.map((d) => (
                    <LengthButton
                      key={d}
                      type="button"
                      role="radio"
                      aria-checked={difficulty === d}
                      $active={difficulty === d}
                      onClick={() => setDifficulty(d)}
                    >
                      {t.difficulty[d]}
                    </LengthButton>
                  ))}
                </LengthRow>
                <DifficultyHint>{t.difficultyHint[difficulty]}</DifficultyHint>
              </>
            )}

            {mode === "daily" && (
              <>
                <DifficultyHint>
                  {dailyPlayedToday ? t.dailyDone : t.modeDesc.daily}
                </DifficultyHint>
                {daily.streak > 0 && (
                  <BestScore>{t.streak(daily.streak)}</BestScore>
                )}
              </>
            )}

            {mode === "classic" &&
              bestScores[difficulty]?.[length] !== undefined && (
                <BestScore>
                  {t.bestScore(bestScores[difficulty][length], length)}
                </BestScore>
              )}

            <PrimaryButton
              type="button"
              onClick={() => startQuiz(mode, length, difficulty)}
            >
              {mode === "daily" && dailyPlayedToday ? t.playAnyway : t.start}
            </PrimaryButton>
          </Card>
        )}

        {phase === "playing" && currentQuestion && (
          <Card>
            <StatusRow>
              {mode === "timeAttack" && (
                <StatusPill $urgent={timeLeft <= 10}>
                  ⏱ {t.timeLeft(timeLeft)}
                </StatusPill>
              )}
              {mode === "survival" && (
                <StatusPill $urgent={lives === 1}>
                  {t.livesLeft(Math.max(0, lives))}
                </StatusPill>
              )}
              {mode === "daily" && <StatusPill>📅 {t.mode.daily}</StatusPill>}
            </StatusRow>

            <ProgressRow>
              <span>
                {isEndless
                  ? t.answered(answeredCount)
                  : t.question(currentIndex + 1, questions.length)}
              </span>
              <span>{score}✓</span>
            </ProgressRow>
            <ProgressTrack>
              <ProgressFill $percent={percent} />
            </ProgressTrack>

            <Prompt>{currentQuestion.prompt}</Prompt>

            {currentQuestion.type === "flag" && (
              <FlagPreview
                src={flagFailed ? FALLBACK_FLAG : currentQuestion.flagUrl}
                alt=""
                onError={() => setFlagFailed(true)}
                decoding="async"
              />
            )}

            {currentQuestion.type === "mapPoint" ? (
              <>
                <DifficultyHint>{t.tapTheMap}</DifficultyHint>
                <MapAnswerPanel>
                  <WorldMap
                    data={countriesData}
                    language={resolvedLanguage}
                    focusedContinent={currentQuestion.mapTargetContinent}
                    showControls={false}
                    fillHeight
                    onCountrySelect={handleMapSelect}
                    highlights={
                      answered
                        ? {
                            correct: currentQuestion.mapTargetName,
                            incorrect: mapWasCorrect
                              ? undefined
                              : mapAnswer?.["english-name"],
                          }
                        : undefined
                    }
                  />
                </MapAnswerPanel>
              </>
            ) : currentQuestion.options.some((option) => option.flagUrl) ? (
              <FlagOptionsGrid role="group" aria-label={currentQuestion.prompt}>
                {currentQuestion.options.map((option, index) => {
                  const state = !answered
                    ? "idle"
                    : index === currentQuestion.correctIndex
                      ? "correct"
                      : index === selectedOption
                        ? "incorrect"
                        : "idle";

                  return (
                    <FlagOptionButton
                      key={option.label}
                      type="button"
                      disabled={answered}
                      $state={state}
                      onClick={() => handleSelect(index)}
                      aria-pressed={selectedOption === index}
                      aria-label={option.label}
                    >
                      <img src={option.flagUrl} alt="" decoding="async" />
                    </FlagOptionButton>
                  );
                })}
              </FlagOptionsGrid>
            ) : (
              <OptionsGrid role="group" aria-label={currentQuestion.prompt}>
                {currentQuestion.options.map((option, index) => {
                  const state = !answered
                    ? "idle"
                    : index === currentQuestion.correctIndex
                      ? "correct"
                      : index === selectedOption
                        ? "incorrect"
                        : "idle";

                  return (
                    <OptionButton
                      key={option.label}
                      type="button"
                      disabled={answered}
                      $state={state}
                      onClick={() => handleSelect(index)}
                      aria-pressed={selectedOption === index}
                    >
                      {option.label}
                    </OptionButton>
                  );
                })}
              </OptionsGrid>
            )}

            <FeedbackText role="status" aria-live="polite">
              {answered
                ? wasCorrect
                  ? t.correct
                  : t.incorrect(
                      currentQuestion.type === "mapPoint"
                        ? currentQuestion.countryName
                        : (currentQuestion.options[currentQuestion.correctIndex]
                            ?.label ?? "")
                    )
                : ""}
            </FeedbackText>

            {answered && (
              <>
                <Explanation>
                  <ExplanationText>
                    {currentQuestion.explanation}
                  </ExplanationText>
                  {currentQuestion.learnMoreCountry && (
                    <ExplanationLink
                      to={`/countries/${currentQuestion.learnMoreCountry}`}
                    >
                      {t.readMore(currentQuestion.learnMoreCountry)}
                    </ExplanationLink>
                  )}
                </Explanation>

                <PrimaryButton type="button" onClick={handleNext}>
                  {mode === "survival" && lives <= 0
                    ? t.seeResult
                    : isLastQuestion
                      ? t.seeResult
                      : t.next}
                </PrimaryButton>
              </>
            )}
          </Card>
        )}

        {phase === "result" && (
          <Card>
            {confettiPieces && <Confetti pieces={confettiPieces} />}
            <CardTitle>
              {endReason === "time"
                ? t.timeUp
                : endReason === "lives"
                  ? t.outOfLives
                  : t.yourScore}
            </CardTitle>
            <ScoreDisplay $celebrate={!!confettiPieces}>
              {score} / {resultTotal}
            </ScoreDisplay>
            <ResultMessage>{resultMessage}</ResultMessage>
            {mode === "daily" && daily.streak > 0 && (
              <BestScore>{t.streak(daily.streak)}</BestScore>
            )}
            <ButtonRow>
              <PrimaryButton
                type="button"
                onClick={() => startQuiz(mode, length, difficulty)}
              >
                {t.tryAgain}
              </PrimaryButton>
              {/* Pops the run's history entry, so leaving the result screen
                  costs the same one "back" as the browser gesture would. */}
              <SecondaryButton type="button" onClick={() => navigate(-1)}>
                {t.changeSettings}
              </SecondaryButton>
              <SecondaryLink to="/">{t.backToMap}</SecondaryLink>
            </ButtonRow>
            {resultQuote && (
              <QuoteBlock>
                <QuoteText>
                  “{isSpanish ? resultQuote.es : resultQuote.en}”
                </QuoteText>
                <QuoteAuthor>
                  {isSpanish ? resultQuote.authorEs : resultQuote.authorEn}
                </QuoteAuthor>
              </QuoteBlock>
            )}
          </Card>
        )}

        <VisuallyHiddenLive aria-live="polite">
          {phase === "playing" &&
            t.question(currentIndex + 1, questions.length)}
        </VisuallyHiddenLive>
      </Content>
    </Page>
  );
};
